import React, { useState } from 'react';
import { loadCurrentState, saveCurrentState } from '../storage';
import { useCheckboxWithTimestamp } from '../hooks/useCheckboxWithTimestamp';

type PrenatalSteroidsOption = 'one' | 'two' | null;
type AntibioticsOption = 'gbs_under4h' | 'gbs_over4h' | 'broad_under4h' | 'broad_over4h' | 'none' | null;

export const useMedicationsStatus = () => {
  const saved = loadCurrentState();
  const medications = saved?.medications || {};

  // Steroids is done if any radio option is selected
  const steroidsIsDone = medications.prenatalSteroidsOption !== null && medications.prenatalSteroidsOption !== undefined;
  // Antibiotics is done if any radio option is selected
  const antibioticsIsDone = medications.maternalAntibioticsOption !== null && medications.maternalAntibioticsOption !== undefined;
  // Magnesium is a checkbox
  const magnesiumIsDone = medications.neuroprotectiveMagnesium;

  const states = [steroidsIsDone, antibioticsIsDone, magnesiumIsDone];
  const checked = states.filter(Boolean).length;
  return { checked, total: 3 };
};

const Medications: React.FC = () => {
  const saved = loadCurrentState();

  const [neuroprotectiveMagnesium, setNeuroprotectiveMagnesium] = useCheckboxWithTimestamp(
    'medications',
    'neuroprotectiveMagnesium',
    saved?.medications?.neuroprotectiveMagnesium || false
  );

  const [prenatalSteroidsOption, setPrenatalSteroidsOptionState] = useState<PrenatalSteroidsOption>(
    saved?.medications?.prenatalSteroidsOption ?? null
  );
  const [antibioticsOption, setAntibioticsOptionState] = useState<AntibioticsOption>(
    saved?.medications?.maternalAntibioticsOption ?? null
  );

  const handlePrenatalSteroidsOption = (option: PrenatalSteroidsOption) => {
    setPrenatalSteroidsOptionState(option);
    saveCurrentState({ medications: { prenatalSteroidsOption: option } }, 'medications');
  };

  const handlePrenatalSteroidsCheckbox = (checked: boolean) => {
    if (!checked) {
      setPrenatalSteroidsOptionState(null);
      saveCurrentState({ medications: { prenatalSteroidsOption: null } }, 'medications');
    }
  };

  const handleAntibioticsOption = (option: AntibioticsOption) => {
    setAntibioticsOptionState(option);
    saveCurrentState({ medications: { maternalAntibioticsOption: option } }, 'medications');
  };

  const subOptionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginRight: '30px',
    marginTop: '8px',
    marginBottom: '4px',
  };

  const radioLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    color: '#103c6e',
    cursor: 'pointer',
  };

  return (
    <div className="section-content-wrapper" style={{ padding: '20px', direction: 'rtl' }}>
      {/* Steroids section - caption with radio buttons */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={prenatalSteroidsOption !== null}
            onChange={(e) => handlePrenatalSteroidsCheckbox(e.target.checked)}
          />
          <span>מתן סטרואידים פרה-נטאלי</span>
        </label>
        <div style={subOptionStyle}>
          <label style={radioLabelStyle}>
            <input
              type="radio"
              name="prenatalSteroidsOption"
              checked={prenatalSteroidsOption === 'one'}
              onChange={() => handlePrenatalSteroidsOption('one')}
            />
            קורס אחד
          </label>
          <label style={radioLabelStyle}>
            <input
              type="radio"
              name="prenatalSteroidsOption"
              checked={prenatalSteroidsOption === 'two'}
              onChange={() => handlePrenatalSteroidsOption('two')}
            />
            שני קורסים
          </label>
        </div>
      </div>

      {/* Antibiotics section - caption with radio buttons */}
      <div className="checkbox-group">
        <div style={{ fontWeight: 500, fontSize: '1rem', color: '#103c6e', marginBottom: '8px' }}>
          מתן אנטיביוטיקה ליולדת
        </div>
        <div style={subOptionStyle}>
          <label style={radioLabelStyle}>
            <input
              type="radio"
              name="antibioticsOption"
              checked={antibioticsOption === 'gbs_under4h'}
              onChange={() => handleAntibioticsOption('gbs_under4h')}
            />
            <span>כיסוי ל <span dir="ltr">GBS</span> פחות מ 4 שעות</span>
          </label>
          <label style={radioLabelStyle}>
            <input
              type="radio"
              name="antibioticsOption"
              checked={antibioticsOption === 'gbs_over4h'}
              onChange={() => handleAntibioticsOption('gbs_over4h')}
            />
            <span>כיסוי ל <span dir="ltr">GBS</span> מעל 4 שעות</span>
          </label>
          <label style={radioLabelStyle}>
            <input
              type="radio"
              name="antibioticsOption"
              checked={antibioticsOption === 'broad_under4h'}
              onChange={() => handleAntibioticsOption('broad_under4h')}
            />
            כיסוי רחב טווח פחות מ 4 שעות
          </label>
          <label style={radioLabelStyle}>
            <input
              type="radio"
              name="antibioticsOption"
              checked={antibioticsOption === 'broad_over4h'}
              onChange={() => handleAntibioticsOption('broad_over4h')}
            />
            כיסוי רחב טווח מעל 4 שעות
          </label>
          <label style={radioLabelStyle}>
            <input
              type="radio"
              name="antibioticsOption"
              checked={antibioticsOption === 'none'}
              onChange={() => handleAntibioticsOption('none')}
            />
            ללא טיפול אנטיביוטי
          </label>
        </div>
      </div>

      {/* Magnesium section - checkbox (3rd item) */}
      <div className="checkbox-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={neuroprotectiveMagnesium}
            onChange={(e) => setNeuroprotectiveMagnesium(e.target.checked)}
          />
          <span>מתן מגנזיום נוירופרוטקטיבי</span>
        </label>
      </div>
    </div>
  );
};

export default Medications;
